import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ViewChild, ChangeDetectorRef, ViewChildren, QueryList } from '@angular/core';
import { UserModel } from '../../customer/models/UserModel';
import { Store, Action } from '@ngrx/store';
import { Actions } from '@ngrx/effects';
import { ActionBase } from '../../core/store/action/ActionBase';
import * as dashboardActions from '../../pillar/dashboard/store/actions';
import { Observable, Subject } from 'rxjs';
import { ModuleConfiguration as CustomerModuleConfiguration } from '../../customer/customer.module.config';
import { UserAccountDetailedStatusModel } from '../../customer/models/UserAccountDetailedStatusModel';
import { LookupModel } from '../../common/models/LookupModel';
import { RoleModel } from '../../security/models/RoleModel';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CurrentUserService } from '../../security/services/CurrentUserService';
import * as fromCustomerStore from '../../customer/store/reducers';
import * as fromSecurityStore from '../../security/store/reducers';
import * as errorActions from '../../pillar/common/actions/ActionError';
import * as userActions from '../../customer/store/actions';
import { AlertsComponent } from '../../pillar/alerts/components/alerts.component';
import { SearchComponent } from '../../pillar/search/components/search.component';
import { QueryAvailableRolesAction, QUERY_AVAILABLE_ROLES_SUCCESS, QUERY_AVAILABLE_ROLES_BY_TYPE_SUCCESS, QueryAvailableRolesByTypeAction } from '../../security/store/actions/index';

@Component({
  selector: 'brd-user-account',
  templateUrl: './user-account.component.html',
  styleUrls: ['./user-account.component.scss']
})
export class UserAccountComponent implements OnInit, OnDestroy {
  user: UserModel;
  userStatus: UserAccountDetailedStatusModel;
  key: string;
  loading$: Observable<boolean>;
  accountActionLoading: boolean;
  errors$: Observable<string[]>;
  assignedRoles$: Observable<RoleModel[]>;
  destroyed$: Subject<boolean> = new Subject<boolean>();
  formArrayInitMethods = new Map<string, () => void>();
  groupLookups$: Observable<LookupModel[]>;
  notificationTypes$: Observable<LookupModel[]>;
  submittingEntities$: Observable<LookupModel[]>;
  userCurrentStatus$: Observable<UserAccountDetailedStatusModel>;
  rolesForm: FormGroup;
  actionIdentifiers: string[] = [];
  loadingAvailable: boolean;
  availableRoles: RoleModel[];
  allRoles: RoleModel[] = [];
  allAssignedRoles: RoleModel[] = [];
  assignedRoles: RoleModel[];
  isEditable: boolean = false;
  showDetailsForAvailable = true;
  showDetailsForAssigned = true;

  editPermission: string = 'CustomerModule.Users:Edit';
  lockPermission: string = 'CustomerModule.Users:Lock';
  unlockPermission: string = 'CustomerModule.Users:Unlock';

  @ViewChild(AlertsComponent)
  alerts: AlertsComponent;

  @ViewChildren(SearchComponent)
  searchs: QueryList<SearchComponent>;

  constructor(private store: Store<any>,
    private actions$: Actions,
    private route: ActivatedRoute,
    private changeDetectorRef: ChangeDetectorRef,
    private currentUserService: CurrentUserService) {
    store.dispatch(new QueryAvailableRolesAction({
      page: 0,
      pageSize: 100
    }));
  }

  pollPostHeightToIFrameParent(height: number = undefined) {
    window.setTimeout(() => {
        var variableHeightElement = document.querySelector("brd-user-account");
        if (variableHeightElement) {
          var currentHeight = variableHeightElement.clientHeight;
          if (height != currentHeight) {
            height = currentHeight;
            this.postHeightToIframeParent(height);
          }
        }
        this.pollPostHeightToIFrameParent(height);
      },
      200);
  }

  postHeightToIframeParent(height: number) {
    window.parent.postMessage({ "height": height, }, "*");
  }

  refreshUser() {
    if (this.key) {
      this.storeDispatch(new dashboardActions.GetSelectionAction(CustomerModuleConfiguration.moduleName, UserModel, { key: this.key }));
    }
  }

  ngOnInit() {
    this.loading$ = this.store.select(fromCustomerStore.getUserListLoading);
    this.errors$ = this.store.select(fromCustomerStore.getUserListErrors);
    this.assignedRoles$ = this.store.select(fromCustomerStore.getUserAssignedRoles);

    this.route.params.take(1).subscribe(p => {
      this.key = p['key'];
      this.refreshUser();
    });

    this.actions$.ofType(errorActions.ACTION_ERROR)
      .takeUntil(this.destroyed$)
      .filter((a: errorActions.ActionError) => this.actionIdentifiers.includes(a.payload.action.identifier))
      .subscribe((a: errorActions.ActionError) => {
        a.payload.errors.forEach(e => this.alerts.addError(e));
      });

    this.actions$.ofType(userActions.LOCK_ACCOUNT_SUCCESS)
      .takeUntil(this.destroyed$)
      .subscribe(() => {
        this.accountActionLoading = false;
        this.alerts.addSuccess('Lock Account Success');
        this.changeDetectorRef.markForCheck();
      });

    this.actions$.ofType(userActions.UNLOCK_ACCOUNT_SUCCESS)
      .takeUntil(this.destroyed$)
      .subscribe(() => {
        this.accountActionLoading = false;
        this.alerts.addSuccess('Unlock Account Success');
        this.changeDetectorRef.markForCheck();
      });

    this.actions$.ofType(userActions.DISABLE_ACCOUNT_SUCCESS)
      .takeUntil(this.destroyed$)
      .subscribe(() => {
        this.accountActionLoading = false;
        this.alerts.addSuccess('Disable Account Success');
        this.changeDetectorRef.markForCheck();
      });

    this.actions$.ofType(userActions.ENABLE_ACCOUNT_SUCCESS)
      .takeUntil(this.destroyed$)
      .subscribe(() => {
        this.accountActionLoading = false;
        this.alerts.addSuccess('Enable Account Success');
        this.changeDetectorRef.markForCheck();
      });

    this.actions$.ofType(userActions.RESET_PASSWORD_SUCCESS)
      .takeUntil(this.destroyed$)
      .subscribe(() => {
        this.accountActionLoading = false;
        this.alerts.addSuccess('Reset Password Success');
        this.changeDetectorRef.markForCheck();
      });

    this.store.select(fromSecurityStore.getAvailableRoles).takeUntil(this.destroyed$)
      .subscribe((roles: RoleModel[]) => {
        this.allRoles = roles;
        this.updateAvailableRoles();
      });

    this.store
      .select(fromCustomerStore.getUserSelection)
      .takeUntil(this.destroyed$)
      .subscribe((v: UserModel) => {
        if (v) {
          this.user = v;
          this.storeDispatch(new userActions.GetUserCurrentStatusAction({ userKey: this.user.key }));
        }
      });

    this.store
      .select(fromCustomerStore.getUserCurrentStatus)
      .takeUntil(this.destroyed$)
      .subscribe((v: UserAccountDetailedStatusModel) => {
        if (v) {
          this.userStatus = v;
          this.changeDetectorRef.markForCheck();
        }
      });

    this.actions$
      .ofType(QUERY_AVAILABLE_ROLES_SUCCESS)
      .takeUntil(this.destroyed$)
      .subscribe(() => {
        this.loadingAvailable = false;
      });

    this.actions$
      .ofType(QUERY_AVAILABLE_ROLES_BY_TYPE_SUCCESS)
      .takeUntil(this.destroyed$)
      .subscribe(() => {
        this.loadingAvailable = false;
      });

    this.assignedRoles$.takeUntil(this.destroyed$)
      .subscribe((roles: RoleModel[]) => {
        this.allAssignedRoles = roles;
        this.updateAssignedRoles(null);
        this.updateAvailableRoles();
      });

    this.userCurrentStatus$ = this.store.select(fromCustomerStore.getUserCurrentStatus);
    this.pollPostHeightToIFrameParent();
    window.addEventListener("message",
      event => {
        this.isEditable = event.data.isEditable;
      },
      false);
  }

  storeDispatch(action: Action) {
    var actionBase = action as ActionBase;
    if (actionBase) {
      this.actionIdentifiers.push(actionBase.identifier);
    }
    this.store.dispatch(action);
  }  

  ngOnDestroy() {
    this.storeDispatch(new dashboardActions.ClearSelectionAction(CustomerModuleConfiguration.moduleName, UserModel));
    this.destroyed$.next(true);
  }
  
  addRole(role) {
    this.storeDispatch(new userActions.AddRoleToUserAction({ userKey: this.user.key, role: role }));
  }

  removeRole(role) {
    this.storeDispatch(new userActions.RemoveRoleFromUserAction({ userKey: this.user.key, role: role }));
  }

  lockAccount() {
    this.accountActionLoading = true;
    this.storeDispatch(new userActions.LockAccountAction({ user: this.user }));
  }

  unlockAccount() {
    this.accountActionLoading = true;
    this.storeDispatch(new userActions.UnlockAccountAction({ user: this.user }));
  }

  private updateAvailableRoles(): void {
    if (this.allRoles) {
      this.availableRoles = this.allAssignedRoles ? this.allRoles.filter(r => this.allAssignedRoles.find(ar => ar.key === r.key) == null) : this.allRoles;
    } else {
      this.availableRoles = undefined;
    }
  }

  initializeSearchFunctions(){
    this.searchs.first.searchControl.valueChanges
      .takeUntil(this.destroyed$)
      .subscribe(() => {
        this.loadingAvailable = true;
        this.store.dispatch(new QueryAvailableRolesAction({
          page: 0,
          pageSize: 100,
          searchTerm: this.searchs.first.searchControl.value
        }));
      });
    this.searchs.last.searchControl.valueChanges
      .takeUntil(this.destroyed$)
      .subscribe(() => {
        this.updateAssignedRoles(this.searchs.last.searchControl.value);
      });
  }

  private updateAssignedRoles(searchTerm) {
    this.assignedRoles = searchTerm ? this.allAssignedRoles.filter(ar => ar.name.toLowerCase().includes(searchTerm.toLowerCase())) : this.allAssignedRoles;
  }

  removeTheRole(role): void {
    if (this.allAssignedRoles.length === 1 && this.currentUserService.userContext.userName === this.user.identifier) {
      this.alerts.addError("Cannot remove all roles.");
    } else {
      this.removeRole(role);
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/frames/user-account/user-account.component.ts